
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface PlanLimits {
  emailAccountsLimit: number | null;
  assistantsLimit: number | null;
  bulkEmailsLimit: number | null;
  requiresSignature: boolean;
  allowsExtendedModel: boolean;
}

export const usePlanRestrictions = () => {
  // Fetch current user's plan details
  const { data: planLimits, isLoading } = useQuery({
    queryKey: ['userPlan'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated user');
      }

      const { data, error } = await supabase.rpc('get_user_plan', {
        p_user_id: session.user.id
      });

      if (error) {
        console.error('Error fetching user plan:', error);
        throw error;
      }

      // Ensure we have default values for free plan limits
      const limits = {
        emailAccountsLimit: data?.email_accounts_limit ?? 1,
        assistantsLimit: data?.assistants_limit ?? 3, // Default free plan assistants limit
        bulkEmailsLimit: data?.bulk_emails_limit ?? 3,
        requiresSignature: data?.requires_signature ?? true,
        allowsExtendedModel: data?.allows_extended_model ?? false
      } as PlanLimits;

      console.log('Retrieved plan limits:', limits);
      return limits;
    }
  });

  // Check if user can add more email accounts
  const canAddEmailAccount = async (): Promise<boolean> => {
    if (!planLimits?.emailAccountsLimit) return true; // null means unlimited

    const { data: accounts, error } = await supabase
      .from('email_accounts')
      .select('id');

    if (error) {
      console.error('Error checking email accounts:', error);
      return false;
    }

    const currentCount = accounts?.length || 0;
    const canAdd = currentCount < planLimits.emailAccountsLimit;

    if (!canAdd) {
      toast.error(
        "Email account limit reached",
        {
          description: "Upgrade your plan to add more email accounts",
          action: {
            label: "Upgrade",
            onClick: () => window.location.href = "/settings?tab=tokens"
          }
        }
      );
    }

    return canAdd;
  };

  // Check if user can add more assistants
  const canAddAssistant = async (): Promise<boolean> => {
    if (!planLimits?.assistantsLimit) return true; // null means unlimited

    const { data: assistants, error } = await supabase
      .from('assistants')
      .select('id');

    if (error) {
      console.error('Error checking assistants:', error);
      return false;
    }

    const currentCount = assistants?.length || 0;
    const canAdd = currentCount < planLimits.assistantsLimit;

    if (!canAdd) {
      toast.error(
        "Assistant limit reached",
        {
          description: "Upgrade your plan to create more assistants",
          action: {
            label: "Upgrade",
            onClick: () => window.location.href = "/settings?tab=tokens"
          }
        }
      );
    }

    return canAdd;
  };

  // Check if user can process bulk emails
  const canProcessBulkEmails = (count: number): boolean => {
    if (!planLimits?.bulkEmailsLimit) return true;

    const canProcess = count <= planLimits.bulkEmailsLimit;

    if (!canProcess) {
      toast.error(
        "Bulk email limit exceeded",
        {
          description: `Your plan allows processing up to ${planLimits.bulkEmailsLimit} emails at once`,
          action: {
            label: "Upgrade",
            onClick: () => window.location.href = "/settings?tab=tokens"
          }
        }
      );
    }

    return canProcess;
  };

  // Check if user can use extended model
  const canUseExtendedModel = (): boolean => {
    return planLimits?.allowsExtendedModel ?? false;
  };

  // Check if email signature is required
  const isSignatureRequired = (): boolean => {
    return planLimits?.requiresSignature ?? true;
  };

  return {
    isLoading,
    planLimits,
    canAddEmailAccount,
    canAddAssistant,
    canProcessBulkEmails,
    canUseExtendedModel,
    isSignatureRequired
  };
};
